/**
 * AgentChatPanel — the shared chat surface for every embedded CRHQ agent
 * (Manifest Agent, R&P Manager, …). All tool-specific copy comes from
 * AgentChatToolConfig; nothing in here may name a specific tool.
 *
 * (Originally the Manifest Agent panel — the conversational replacement for
 * the PDF wizard.)
 *
 * Full-screen experience (same early-return pattern as CreateManifestWizard):
 * chat with the CRHQ Flight Manifest Manager on the left, the agent's live
 * status board + artifacts on the right. Streaming rides the SSE relay
 * (message.partial live preview, superseded by the full transcript), uploads
 * are dual-written (R2 manifest rows + agent session files) server-side.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
// GFM (tables, strikethrough, task lists) + breaks: the agent writes GitHub-
// flavored markdown — without these plugins its tables render as raw
// pipe-soup in one paragraph (2026-09-18 staging report).
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { PageContainer } from '@/components/ui/PageContainer';
import { PageHeader } from '@/components/ui/PageHeader';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog/ConfirmDialog';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAgentVoiceStore } from '@/stores/agentVoiceStore';
import { VoiceButton } from './VoiceButton';
import { appendAnswer, attachBoardBridge, boardFrameDoc, boardTitle, carriesAnswer, newBridgeToken, splitButtonMarker, withButtonMarker } from './boardBridge';
import {
	AgentChatService,
	type AgentArtifactDTO,
	type AgentChatUploadedFileDTO,
	type AgentQueueItemDTO,
	type AgentTranscriptMessage,
} from '@/services/AgentChatService';
import {
	ArrowLeft, Bot, Download, FileText, Loader2, Paperclip, Play, RefreshCw, Send, Square, X, LayoutDashboard,
} from 'lucide-react';

/**
 * Per-tool configuration — everything tool-specific about the chat surface.
 * Keep in sync with the server adapter (server/modules/agent-chat/toolAdapters.ts).
 */
export interface AgentChatToolConfig {
	/** agent_chat_links tool key (must have a server adapter). */
	tool: string;
	/** Header title; receives the event name once connected. */
	title: (eventName: string) => string;
	headerDescription: string;
	backLabel: string;
	composerPlaceholder: string;
	/** Display name used in error copy ("Could not reach the …"). */
	agentDisplayName: string;
	/** localStorage key for the split percentage (per tool). */
	splitStorageKey: string;
	/** Empty-chat hint: what to drop / an example question. */
	emptyStateHint: React.ReactNode;
	/** Overlay label while dragging files onto the chat. */
	dropLabel: string;
	/** Placeholder text for the status-board pane before the agent writes one. */
	boardPlaceholder: string;
}

interface Props {
	eventId: string;
	onClose: () => void;
	config: AgentChatToolConfig;
}

// Server-composed markers stripped from user bubbles (all tools).
const ATTACH_MARKERS = [
	'[Attached files — Moniker flight-manifest batch]',
	'[Attached files — Moniker agent batch]',
];
// Every tool's event-context marker (R&P used the manifest one until 2026-10-13).
const CONTEXT_MARKERS = [
	'[Event context — Moniker flight-manifests]',
	'[Event context — Moniker research & proposal]',
	'[Event context — Moniker flight cost]',
];

/** User-role rows the platform or the Cockpit server sends to the agent — never shown. */
const SYSTEM_MESSAGE_MARKERS = ['[agent-setup]', '[run-complete]'];
const isSystemMessage = (raw: string) => SYSTEM_MESSAGE_MARKERS.some(m => raw.trimStart().startsWith(m));

function blocksText(m: AgentTranscriptMessage): string {
	if (typeof m.content === 'string') return m.content;
	// The platform stores message text as JSON, so a reply that is only a
	// number ("1") comes back as the number 1. Anything that is not a list of
	// blocks is shown as text (2026-10-14: Scottie answered "1" and the chat
	// crashed on every load).
	if (!Array.isArray(m.content)) {
		const c = m.content as unknown;
		return c == null ? '' : typeof c === 'object' ? JSON.stringify(c) : String(c);
	}
	return m.content
		.filter(b => b.type === 'text')
		.map(b => b.text || '')
		.join('\n');
}

/** Split a composed user message into visible text + attached file names. */
function parseUserMessage(raw: string): { text: string; files: string[] } {
	// Context block (first message on a link) is plumbing — never shown.
	const ctx = Math.min(...CONTEXT_MARKERS.map(m => raw.indexOf(m)).filter(i => i !== -1).concat([Infinity]));
	if (Number.isFinite(ctx)) raw = raw.slice(0, ctx).trimEnd();
	const idx = Math.min(...ATTACH_MARKERS.map(m => raw.indexOf(m)).filter(i => i !== -1).concat([Infinity]));
	if (!Number.isFinite(idx)) return { text: raw, files: [] };
	const text = raw.slice(0, idx).trim();
	const files = raw
		.slice(idx)
		.split('\n')
		.filter(l => l.startsWith('- '))
		.map(l => l.slice(2).split(' → ')[0].trim())
		.filter(Boolean);
	return { text, files };
}

/** Translate internal tool names into calm, user-facing activity language. */
function activityLabel(tool: string): string {
	const t = tool.toLowerCase();
	if (t === 'read' || t === 'glob' || t === 'grep' || t === 'ls') return 'Reading the documents';
	if (t === 'write' || t === 'edit' || t === 'notebookedit') return 'Preparing results';
	if (t === 'bash') return 'Working through the data';
	if (t.includes('web') || t.includes('fetch') || t.includes('search')) return 'Looking something up';
	if (t.includes('task') || t.includes('agent') || t.includes('deleg')) return 'Coordinating helpers';
	return 'Working';
}

/**
 * Moniker-native board presentation: strip the platform's own freshness banner
 * (we render an "Updated X ago" chip in the panel chrome instead) and give the
 * document breathing room at the bottom.
 */
function sanitizeBoardHtml(html: string): string {
	const stripped = html.replace(/<div id="crhq-board-freshness"[^>]*>[\s\S]*?<\/div>\n?/, '');
	const pad = '<style>body{padding-bottom:36px !important}</style>';
	return stripped.includes('</body>') ? stripped.replace('</body>', `${pad}</body>`) : stripped + pad;
}

function timeAgo(iso: string | undefined, now: number): string {
	if (!iso) return '';
	const t = new Date(iso).getTime();
	if (!Number.isFinite(t)) return '';
	const sec = Math.max(0, Math.floor((now - t) / 1000));
	if (sec < 45) return 'just now';
	const min = Math.floor(sec / 60);
	if (min < 60) return `${min}m ago`;
	const hr = Math.floor(min / 60);
	if (hr < 24) return `${hr}h ago`;
	const d = Math.floor(hr / 24);
	return d === 1 ? 'yesterday' : `${d}d ago`;
}

/** Message timestamp (CRHQ sends createdAt; tolerate snake_case too). */
function msgTime(m: AgentTranscriptMessage): string | undefined {
	return m.createdAt || m.created_at;
}

/** User-role transcript rows that are actually deliveries FROM helper agents. */
function isHelperDelivery(text: string): boolean {
	return text.includes('═══ BATCHED QUEUE DELIVERY') || text.startsWith('📨 Message from session');
}

/**
 * Download an artifact from the PARENT page (Rogo session, 2026-09-20): in-iframe
 * download links are unreliable across browsers even with allow-downloads, and
 * right-click-open was the only workaround. A Blob + anchor click here always
 * shows the browser's save flow, and the content already arrived authenticated
 * through the relay — nothing is ever exposed on a public URL.
 */
function downloadArtifact(a: AgentArtifactDTO) {
	const title = (a.title || 'artifact').trim();
	const hasExt = /\.[A-Za-z0-9]{1,8}$/.test(title);
	const isHtml = a.artifactType === 'html';
	const name = hasExt ? title : `${title}${isHtml ? '.html' : '.md'}`;
	const ext = name.split('.').pop()?.toLowerCase();
	const mime =
		ext === 'csv' ? 'text/csv' :
		ext === 'html' ? 'text/html' :
		ext === 'json' ? 'application/json' :
		'text/plain';
	const blob = new Blob([a.content ?? ''], { type: `${mime};charset=utf-8` });
	const url = URL.createObjectURL(blob);
	const el = document.createElement('a');
	el.href = url;
	el.download = name;
	document.body.appendChild(el);
	el.click();
	el.remove();
	setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** True for transcript rows that are tool plumbing, not something a person said. */
function isToolResultMessage(m: AgentTranscriptMessage): boolean {
	return Array.isArray(m.content) && m.content.some(b => b.type === 'tool_result');
}

export function AgentChatPanel({ eventId, onClose, config }: Props) {
	const svc = useMemo(() => new AgentChatService(), []);
	const notifyError = useNotificationStore(s => s.error);

	// Connection
	const [linkId, setLinkId] = useState<string | null>(null);
	const [boardArtifactId, setBoardArtifactId] = useState<string | null>(null);
	const [eventName, setEventName] = useState('');
	const [connectError, setConnectError] = useState<string | null>(null);

	// Conversation
	const [messages, setMessages] = useState<AgentTranscriptMessage[]>([]);
	const [isProcessing, setIsProcessing] = useState(false);
	const [partialText, setPartialText] = useState('');
	const [thinking, setThinking] = useState(false);
	const [activity, setActivity] = useState<{ steps: number; label: string } | null>(null);
	const [turnStartedAt, setTurnStartedAt] = useState<number | null>(null);
	const [elapsed, setElapsed] = useState(0);
	const [queuedNote, setQueuedNote] = useState(false);
	const [queueItems, setQueueItems] = useState<AgentQueueItemDTO[]>([]);
	const [helperUpdateCount, setHelperUpdateCount] = useState(0);
	const [helpersCount, setHelpersCount] = useState(0);

	// Composer
	const [input, setInput] = useState('');
	const [stagedFiles, setStagedFiles] = useState<File[]>([]);
	const [sending, setSending] = useState(false);
	const [dragOver, setDragOver] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	// Grow the message box with its text (dictated voice notes arrive as one
	// long line), up to the max-h-40 cap; it scrolls beyond that.
	useEffect(() => {
		const el = textareaRef.current;
		if (!el) return;
		el.style.height = 'auto';
		el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
	}, [input]);

	// Voice input — the same mic as the CRHQ session UI (store + button ported
	// from control-room). Transcribed text is appended to the message box; the
	// user still reviews it and presses Send.
	const {
		voiceState, recordingDuration, showRetryBanner, audioDevices, selectedDeviceId,
		startRecording, stopRecording, retryTranscription, setSelectedDevice,
	} = useAgentVoiceStore();
	const appendTranscript = useCallback((text: string) => {
		const t = text.trim();
		if (!t) return;
		setInput(prev => (prev.trim() ? `${prev.replace(/\s+$/, '')} ${t}` : t));
	}, []);
	useEffect(() => {
		const voice = useAgentVoiceStore.getState();
		voice.setTransport(linkId ? {
			transcribe: (audio) => svc.transcribe(linkId, audio),
			retry: (recordingId) => svc.retryTranscription(linkId, recordingId),
		} : null);
	}, [linkId, svc]);
	useEffect(() => {
		const voice = useAgentVoiceStore.getState();
		voice.setOnTranscriptionComplete(appendTranscript);
		voice.setOnTranscriptionError((message) => notifyError(message));
		void voice.enumerateDevices();
		return () => {
			// Leaving the chat mid-recording: release the microphone, drop the audio.
			const v = useAgentVoiceStore.getState();
			v.cancelRecording();
			v.setOnTranscriptionComplete(null);
			v.setOnTranscriptionError(null);
			v.setTransport(null);
			v.setShowRetryBanner(false);
		};
	}, [appendTranscript, notifyError]);
	const handleVoiceClick = async () => {
		if (voiceState === 'recording') {
			stopRecording();
		} else if (voiceState === 'idle') {
			const started = await startRecording();
			if (!started) notifyError('Could not access the microphone. Please check your browser permissions for this site.');
		}
	};
	const handleVoiceRetry = async () => {
		const text = await retryTranscription();
		if (text) appendTranscript(text);
	};

	// Right panel
	const [boardHtml, setBoardHtml] = useState<string | null>(null);
	const [boardUpdatedAt, setBoardUpdatedAt] = useState<string | undefined>(undefined);
	const [nowTick, setNowTick] = useState(() => Date.now());
	const [artifacts, setArtifacts] = useState<AgentArtifactDTO[]>([]);
	const [selectedArtifact, setSelectedArtifact] = useState<AgentArtifactDTO | null>(null);
	const [hiddenTabIds, setHiddenTabIds] = useState<Set<string>>(new Set());
	const [mobileView, setMobileView] = useState<'chat' | 'board'>('chat');
	const [rotateOpen, setRotateOpen] = useState(false);

	// Desktop split (chat % width). 50/50 default, user-resizable, persisted.
	const [splitPct, setSplitPct] = useState<number>(() => {
		const v = Number(localStorage.getItem(config.splitStorageKey) || '50');
		return Number.isFinite(v) && v >= 30 && v <= 70 ? v : 50;
	});
	const [dragging, setDragging] = useState(false);
	const splitRef = useRef<HTMLDivElement>(null);

	const bottomRef = useRef<HTMLDivElement>(null);
	const messagesRef = useRef<HTMLDivElement>(null);
	const [atBottom, setAtBottom] = useState(true);
	const dragDepth = useRef(0);
	const lastSendAt = useRef(0);
	const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const esRef = useRef<EventSource | null>(null);

	// ── Bootstrap: ensure link ──
	useEffect(() => {
		let gone = false;
		(async () => {
			try {
				const d = await svc.ensure(config.tool, eventId);
				if (gone) return;
				setLinkId(d.linkId);
				setBoardArtifactId(d.boardArtifactId);
				setEventName(d.eventName);
				try {
					const saved = JSON.parse(localStorage.getItem(`agent-chat-hidden-tabs-${d.linkId}`) || '[]');
					if (Array.isArray(saved)) setHiddenTabIds(new Set(saved));
				} catch { /* fresh */ }
			} catch (e) {
				if (!gone) setConnectError(e instanceof Error ? e.message : `Could not reach the ${config.agentDisplayName}`);
			}
		})();
		return () => { gone = true; };
	}, [svc, eventId]);

	// Messages the person just sent stay on screen until the transcript (or the
	// queue, when the agent is busy) holds them: the refetch right after Send
	// often lands before the platform has stored the new message, and replacing
	// the list then made the person's own bubble vanish until a reload (2026-10-26).
	const pendingSent = useRef<Array<{ content: string; key: string; at: number }>>([]);
	const queueTexts = useRef<string[]>([]);
	const sentKey = (text: string) => text.split('\n\n[')[0].trim().slice(0, 120);
	const withPending = useCallback((list: AgentTranscriptMessage[]) => {
		const userTexts = list.filter(m => m.role === 'user').map(m => (typeof m.content === 'string' ? m.content : blocksText(m)));
		pendingSent.current = pendingSent.current.filter(p =>
			Date.now() - p.at < 180_000 &&
			!userTexts.some(t => t.includes(p.key)) &&
			!queueTexts.current.some(t => t.includes(p.key) || p.key.includes(t.trim().slice(0, 60))));
		return pendingSent.current.length ? [...list, ...pendingSent.current.map(p => ({ role: 'user', content: p.content } as AgentTranscriptMessage))] : list;
	}, []);

	// ── Transcript refetch (debounced; the transcript is the source of truth) ──
	const refetchTranscript = useCallback((immediate = false) => {
		if (!linkId) return;
		if (refetchTimer.current) clearTimeout(refetchTimer.current);
		refetchTimer.current = setTimeout(async () => {
			try {
				const d = await svc.transcript(linkId);
				setMessages(withPending(d.messages));
				if (d.isProcessing) {
					setIsProcessing(true);
				} else if (Date.now() - lastSendAt.current > 6000) {
					// Only trust a transcript "idle" once the just-sent turn had time
					// to register — otherwise the indicator blinks off and on again.
					setIsProcessing(false);
				}
			} catch { /* transient */ }
		}, immediate ? 0 : 350);
	}, [svc, linkId]);

	const refreshBoard = useCallback(async () => {
		if (!linkId || !boardArtifactId) return;
		const a = await svc.artifact(linkId, boardArtifactId).catch(() => null);
		if (a?.content) {
			setBoardHtml(sanitizeBoardHtml(a.content));
			setBoardUpdatedAt((a.updatedAt || a.updated_at) as string | undefined);
		}
	}, [svc, linkId, boardArtifactId]);

	const refreshArtifacts = useCallback(async () => {
		if (!linkId) return;
		const list = await svc.artifacts(linkId).catch(() => []);
		setArtifacts(list.filter(a => a.id !== boardArtifactId));
	}, [svc, linkId, boardArtifactId]);

	const refreshQueue = useCallback(async () => {
		if (!linkId) return;
		const items = await svc.queue(linkId).catch(() => []);
		const pending = items.filter(i => !i.status || i.status === 'pending' || i.status === 'queued');
		// Only the USER's own queued messages belong in the conversation (they
		// carry Run now / Cancel). Helper-to-agent traffic is internal plumbing:
		// it becomes a quiet count on the helpers strip, never a chat bubble.
		const mine = pending.filter(i => !i.fromAgent && !i.system);
		queueTexts.current = mine.map(i => String(i.message ?? i.preview ?? '')).filter(Boolean);
		setQueueItems(mine);
		setHelperUpdateCount(pending.filter(i => i.fromAgent && !i.system).length);
	}, [svc, linkId]);

	const refreshHelpers = useCallback(async () => {
		if (!linkId) return;
		const h = await svc.helpers(linkId).catch(() => ({ count: 0, helpers: [] }));
		setHelpersCount(h.count);
	}, [svc, linkId]);

	const setHiddenTabs = useCallback((next: Set<string>) => {
		setHiddenTabIds(next);
		if (linkId) {
			try { localStorage.setItem(`agent-chat-hidden-tabs-${linkId}`, JSON.stringify([...next])); } catch { /* quota */ }
		}
	}, [linkId]);

	const closeTab = useCallback((id: string) => {
		setSelectedArtifact(prev => (prev?.id === id ? null : prev));
		setHiddenTabs(new Set([...hiddenTabIds, id]));
	}, [hiddenTabIds, setHiddenTabs]);

	// ── Initial load + board polling fallback ──
	useEffect(() => {
		if (!linkId) return;
		refetchTranscript(true);
		void refreshBoard();
		void refreshArtifacts();
		void refreshQueue();
		void refreshHelpers();
		const t = setInterval(() => {
			if (!document.hidden) { void refreshBoard(); void refreshHelpers(); void refreshQueue(); }
		}, isProcessing ? 5000 : 15000);
		return () => clearInterval(t);
	}, [linkId, isProcessing, refetchTranscript, refreshBoard, refreshArtifacts, refreshQueue, refreshHelpers]);

	// ── Live stream (SSE via relay; EventSource auto-reconnects) ──
	useEffect(() => {
		if (!linkId) return;
		const es = new EventSource(svc.streamUrl(linkId));
		esRef.current = es;

		const onJson = (e: MessageEvent): any => {
			try { return JSON.parse(e.data); } catch { return {}; }
		};

		es.addEventListener('turn.started', () => {
			setIsProcessing(true);
			setActivity(null);
			setTurnStartedAt(Date.now());
			lastSendAt.current = Date.now();
			setQueuedNote(false);
			void refreshQueue();
		});
		es.addEventListener('message.partial', (e) => {
			const d = onJson(e as MessageEvent);
			if (typeof d.text === 'string') {
				setThinking(false);
				setPartialText(prev => prev + d.text);
			}
		});
		es.addEventListener('message.delta', () => {
			// The delta carries the COMPLETE block — the transcript supersedes our preview.
			setPartialText('');
			refetchTranscript();
		});
		es.addEventListener('message.thinking', () => setThinking(true));
		es.addEventListener('message.user', () => refetchTranscript());
		es.addEventListener('tool.started', (e) => {
			const d = onJson(e as MessageEvent);
			const name = String(d.tool || d.name || 'tool');
			setIsProcessing(true);
			setThinking(false);
			setTurnStartedAt(prev => prev ?? Date.now());
			setActivity(prev => ({ steps: (prev?.steps ?? 0) + 1, label: activityLabel(name) }));
			// Helpers are spawned inside tool calls — re-check promptly so the
			// "N helpers working" widget appears as soon as they exist.
			if (/task|agent|deleg/i.test(name)) void refreshHelpers();
		});
		const endTurn = () => {
			// A finished turn means the platform has stored what was sent; queued
			// sends show in the queue strip. Never risk a doubled bubble.
			pendingSent.current = [];
			setIsProcessing(false);
			setPartialText('');
			setThinking(false);
			setActivity(null);
			setTurnStartedAt(null);
			setQueuedNote(false);
			refetchTranscript();
			void refreshBoard();
			void refreshArtifacts();
			void refreshQueue();
			void refreshHelpers();
		};
		es.addEventListener('turn.completed', endTurn);
		es.addEventListener('turn.stopped', endTurn);
		es.addEventListener('session.idle', () => setIsProcessing(false));
		es.addEventListener('queue.enqueued', () => { setQueuedNote(true); void refreshQueue(); });
		es.addEventListener('queue.updated', () => void refreshQueue());
		es.addEventListener('queue.processing', () => {
			setQueuedNote(false);
			setIsProcessing(true);
			void refreshQueue();
		});
		es.addEventListener('artifact.saved', (e) => {
			const d = onJson(e as MessageEvent);
			if (d.artifactId && boardArtifactId && d.artifactId === boardArtifactId) void refreshBoard();
			else {
				void refreshArtifacts();
				// The agent updated it — surface it again even if the user closed the tab.
				if (d.artifactId) {
					setHiddenTabIds(prev => {
						if (!prev.has(d.artifactId)) return prev;
						const next = new Set(prev);
						next.delete(d.artifactId);
						if (linkId) { try { localStorage.setItem(`agent-chat-hidden-tabs-${linkId}`, JSON.stringify([...next])); } catch { /* quota */ } }
						return next;
					});
				}
			}
		});
		es.addEventListener('session.error', (e) => {
			const d = onJson(e as MessageEvent);
			notifyError(typeof d.error === 'string' ? `Agent error: ${d.error}` : 'The agent hit an error — see the conversation.');
			setIsProcessing(false);
		});
		es.addEventListener('gap', () => refetchTranscript(true));

		return () => {
			es.close();
			esRef.current = null;
		};
	}, [linkId, svc, boardArtifactId, refetchTranscript, refreshBoard, refreshArtifacts, refreshQueue, refreshHelpers, notifyError]);

	// ── Split drag (desktop only; iframes eat pointer events, hence `dragging` overlay state) ──
	useEffect(() => {
		if (!dragging) return;
		const onMove = (e: PointerEvent) => {
			const rect = splitRef.current?.getBoundingClientRect();
			if (!rect || rect.width === 0) return;
			const pct = Math.min(70, Math.max(30, ((e.clientX - rect.left) / rect.width) * 100));
			setSplitPct(pct);
		};
		const onUp = () => {
			setDragging(false);
			setSplitPct(p => {
				try { localStorage.setItem(config.splitStorageKey, String(Math.round(p))); } catch { /* quota */ }
				return p;
			});
		};
		window.addEventListener('pointermove', onMove);
		window.addEventListener('pointerup', onUp, { once: true });
		return () => {
			window.removeEventListener('pointermove', onMove);
			window.removeEventListener('pointerup', onUp);
		};
	}, [dragging]);

		// ── Freshness chip ticker ──
	useEffect(() => {
		const t = setInterval(() => setNowTick(Date.now()), 30000);
		return () => clearInterval(t);
	}, []);

		// ── Autoscroll (only while the user is pinned to the latest messages) ──
	useEffect(() => {
		if (atBottom) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages.length, partialText, isProcessing, atBottom]);

	// ── Working-time ticker ──
	useEffect(() => {
		if (!isProcessing || !turnStartedAt) { setElapsed(0); return; }
		const t = setInterval(() => setElapsed(Math.floor((Date.now() - turnStartedAt) / 1000)), 1000);
		return () => clearInterval(t);
	}, [isProcessing, turnStartedAt]);

	// ── Status-board answer buttons (boardBridge.ts) ──
	// A fresh token for every board document; the board's crhq.prefill(text)
	// ADDS a line to the message box (never sends). Answers still in the text at
	// Send get CRHQ's label line so the agent knows a button was used.
	const boardFrameRef = useRef<HTMLIFrameElement>(null);
	const boardAnswers = useRef<string[]>([]);
	const boardDoc = useMemo(() => {
		if (!boardHtml) return { html: '', token: '', title: '' };
		const token = newBridgeToken();
		return { html: boardFrameDoc(boardHtml, token), token, title: boardTitle(boardHtml) };
	}, [boardHtml]);
	const boardRef = useRef(boardDoc);
	boardRef.current = boardDoc;
	const boardVisible = useRef(false);
	boardVisible.current = !!boardHtml && !selectedArtifact;
	useEffect(() => attachBoardBridge({
		getFrame: () => boardFrameRef.current,
		getToken: () => boardRef.current.token,
		check: () => ({ ok: boardVisible.current && !!linkId, page: boardRef.current.title }),
		onPrefill: (text) => {
			boardAnswers.current = [...boardAnswers.current, text].slice(-20);
			setInput((prev) => appendAnswer(prev, text));
			setMobileView('chat'); // phones: show the box the answer went into
			setTimeout(() => {
				const el = textareaRef.current;
				if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
			}, 50);
			return true;
		},
	}), [linkId]);

	// ── Send ──
	const send = async () => {
		const text = input.trim();
		if ((!text && stagedFiles.length === 0) || !linkId || sending) return;
		setSending(true);
		try {
			let attachments: AgentChatUploadedFileDTO[] | undefined;
			if (stagedFiles.length > 0) {
				const up = await svc.upload(linkId, stagedFiles);
				attachments = up.files;
			}
			const answered = text && carriesAnswer(text, boardAnswers.current);
			const content = answered ? withButtonMarker(text, boardRef.current.title) : (text || 'Please process the attached files.');
			boardAnswers.current = [];
			// Optimistic echo; kept until the transcript or queue holds it (withPending)
			const echo = attachments ? `${content}\n\n${ATTACH_MARKERS[0]}\n${attachments.map(a => `- ${a.originalName} → x`).join('\n')}` : content;
			pendingSent.current = [...pendingSent.current, { content: echo, key: sentKey(content), at: Date.now() }];
			setMessages(prev => [...prev, { role: 'user', content: echo }]);
			setInput('');
			setStagedFiles([]);
			setIsProcessing(true);
			lastSendAt.current = Date.now();
			setAtBottom(true);
			await svc.sendMessage(linkId, content, attachments);
			refetchTranscript();
			void refreshQueue();
		} catch (e) {
			notifyError(e instanceof Error ? `Send failed: ${e.message}` : 'Could not send the message');
			setIsProcessing(false);
		} finally {
			setSending(false);
		}
	};

	const stop = async () => {
		if (!linkId) return;
		await svc.stop(linkId).catch(() => undefined);
	};

	const addFiles = (list: FileList | File[]) => {
		const incoming = Array.from(list);
		setStagedFiles(prev => [...prev, ...incoming].slice(0, 30));
	};

	const doRotate = async () => {
		if (!linkId) return;
		try {
			const d = await svc.rotate(linkId);
			setLinkId(d.linkId);
			setBoardArtifactId(d.boardArtifactId);
			setMessages([]);
			setBoardHtml(null);
			setArtifacts([]);
			setSelectedArtifact(null);
		} catch (e) {
			notifyError(e instanceof Error ? `Could not start a fresh chat: ${e.message}` : 'Could not start a fresh chat');
		} finally {
			setRotateOpen(false);
		}
	};

	// ── Visible messages ──
	// Tool results ride the transcript as user-role messages with tool_result
	// blocks (API convention) — they are plumbing, never rendered. A user
	// bubble must carry visible text or attachment chips; an assistant bubble
	// must carry text.
	const visible = useMemo(
		() =>
			messages.filter(m => {
				if (isToolResultMessage(m)) return false;
				if (m.role === 'user') {
					const raw = typeof m.content === 'string' ? m.content : blocksText(m);
					if (isSystemMessage(raw)) return false;
					// Helper reports arrive as user-role rows — internal plumbing the
					// event planner has no use for. The helpers strip is their surface.
					if (isHelperDelivery(raw)) return false;
					const { text, files } = parseUserMessage(raw);
					return text.trim().length > 0 || files.length > 0;
				}
				return m.role === 'assistant' && blocksText(m).trim().length > 0;
			}),
		[messages],
	);

	// ── Render helpers ──
	const renderMessage = (m: AgentTranscriptMessage, i: number) => {
		const stamp = timeAgo(msgTime(m), nowTick);
		if (m.role === 'user') {
			const raw = typeof m.content === 'string' ? m.content : blocksText(m);
			const parsed = parseUserMessage(raw);
			const { text, page: buttonPage } = splitButtonMarker(parsed.text);
			const files = parsed.files;
			return (
				<div key={i} className="flex flex-col items-end">
					<div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary-600 px-4 py-2.5 text-sm whitespace-pre-wrap text-white">
						{text}
						{files.length > 0 && (
							<div className="mt-2 flex flex-wrap gap-1.5">
								{files.map((f, j) => (
									<span key={j} className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-xs">
										<FileText className="w-3 h-3" /> {f}
									</span>
								))}
							</div>
						)}
					</div>
					{(stamp || buttonPage) && (
						<div className="mt-1 pr-1 text-[11px] text-gray-400" title={buttonPage ? `Answered with a button on "${buttonPage}"` : undefined}>
							{buttonPage ? 'Answered with a button' : ''}{buttonPage && stamp ? ' · ' : ''}{stamp}
						</div>
					)}
				</div>
			);
		}
		const text = blocksText(m);
		return (
			<div key={i} className="flex gap-2.5">
				<div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
					<Bot className="w-4 h-4" />
				</div>
				<div className="flex max-w-[85%] flex-col items-start">
					<div className="rounded-2xl rounded-bl-md border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800">
						{text ? (
							<div className="text-sm leading-relaxed [&_p]:my-1.5 [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:text-[12px] [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-gray-900 [&_pre]:p-2 [&_pre]:text-gray-100 [&_table]:my-2 [&_table]:w-full [&_table]:text-xs [&_th]:border [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1 [&_a]:text-primary-600 [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-500">
								<ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{text}</ReactMarkdown>
							</div>
						) : null}
					</div>
					{stamp && <div className="mt-1 pl-1 text-[11px] text-gray-400">{stamp}</div>}
				</div>
			</div>
		);
	};

	// ── Connect error / loading states ──
	if (connectError) {
		return (
			<PageContainer>
				<PageHeader title={config.agentDisplayName} description="Connection problem">
					<Button variant="outline" onClick={onClose} className="gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button>
				</PageHeader>
				<div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
					{connectError}
				</div>
			</PageContainer>
		);
	}

	const visibleTabs = useMemo(
		() =>
			[...artifacts]
				.filter(a => !hiddenTabIds.has(a.id))
				.sort((a, b) => String(b.updatedAt || b.updated_at || '').localeCompare(String(a.updatedAt || a.updated_at || ''))),
		[artifacts, hiddenTabIds],
	);

	const rightPanel = (
		<div className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
			<div className="flex items-center gap-1 border-b border-gray-200 px-2 py-1.5 overflow-x-auto">
				<button
					onClick={() => setSelectedArtifact(null)}
					className={`rounded-md px-2.5 py-1 text-xs font-medium whitespace-nowrap ${!selectedArtifact ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-50'}`}
				>
					<LayoutDashboard className="mr-1 inline w-3.5 h-3.5" /> Status
				</button>
				{visibleTabs.map(a => (
					<span
						key={a.id}
						className={`group inline-flex items-center gap-0.5 rounded-md pl-2.5 pr-1 py-1 text-xs font-medium whitespace-nowrap ${selectedArtifact?.id === a.id ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-50'}`}
					>
						<button
							onClick={async () => {
								const full = linkId ? await svc.artifact(linkId, a.id) : null;
								setSelectedArtifact(full ?? a);
							}}
							title={a.title}
						>
							{(a.title || 'Artifact').slice(0, 22)}
						</button>
						<button
							onClick={(e) => { e.stopPropagation(); closeTab(a.id); }}
							className="rounded p-0.5 text-gray-300 hover:bg-gray-200 hover:text-gray-600 group-hover:text-gray-400"
							title="Close tab (the agent can bring it back by updating it)"
						>
							<X className="h-3 w-3" />
						</button>
					</span>
				))}
				{hiddenTabIds.size > 0 && (
					<button
						onClick={() => setHiddenTabs(new Set())}
						className="rounded-md px-2 py-1 text-[11px] text-gray-400 hover:bg-gray-50 hover:text-gray-600 whitespace-nowrap"
						title="Show closed tabs"
					>
						+{hiddenTabIds.size} closed
					</button>
				)}
				<div className="ml-auto flex items-center gap-2">
					{(() => {
						const shown = selectedArtifact
							? timeAgo((selectedArtifact.updatedAt || selectedArtifact.updated_at) as string | undefined, nowTick)
							: timeAgo(boardUpdatedAt, nowTick);
						return shown ? <span className="whitespace-nowrap text-[11px] text-gray-400">Updated {shown}</span> : null;
					})()}
					{selectedArtifact?.content && (
						<button
							onClick={() => downloadArtifact(selectedArtifact)}
							className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1 text-xs font-semibold text-white hover:bg-green-700"
							title="Download this file to your computer"
						>
							<Download className="w-3.5 h-3.5" /> Download
						</button>
					)}
					<button onClick={() => { void refreshBoard(); void refreshArtifacts(); }} className="rounded-md p-1 text-gray-400 hover:bg-gray-50" title="Refresh">
						<RefreshCw className="w-3.5 h-3.5" />
					</button>
				</div>
			</div>
			<div className="flex-1 overflow-hidden bg-gray-50">
				{selectedArtifact ? (
					selectedArtifact.artifactType === 'html' && selectedArtifact.content ? (
						// allow-downloads (2026-09-20, Rogo session): the agent delivers CSVs
						// as a data-URI <a download> button inside the artifact. The empty
						// sandbox blocked every download, which pushed exports onto PUBLIC
						// unauthenticated URLs. Still no scripts; the board iframe stays
						// fully sandboxed.
						<iframe title={selectedArtifact.title || 'Artifact'} sandbox="allow-downloads" srcDoc={selectedArtifact.content} className="h-full w-full border-0 bg-white" />
					) : (
						<div className="h-full overflow-auto p-4 text-sm">
							<div className="text-sm leading-relaxed [&_p]:my-1.5 [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:text-[12px] [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-gray-900 [&_pre]:p-2 [&_pre]:text-gray-100 [&_table]:my-2 [&_table]:w-full [&_table]:text-xs [&_th]:border [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1 [&_a]:text-primary-600 [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-500">
								<ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{selectedArtifact.content || '*No content*'}</ReactMarkdown>
							</div>
						</div>
					)
				) : boardHtml ? (
					// allow-scripts WITHOUT allow-same-origin (2026-10-26): the board's answer
					// buttons call crhq.prefill (boardBridge.ts). Opaque origin: the board
					// cannot reach the app, its cookies or its API.
					<iframe ref={boardFrameRef} title="Agent status board" sandbox="allow-scripts" srcDoc={boardDoc.html} className="h-full w-full border-0 bg-white" />
				) : (
					<div className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-400">
						{config.boardPlaceholder}
					</div>
				)}
			</div>
		</div>
	);

	return (
		<PageContainer>
			<PageHeader
				title={eventName ? config.title(eventName) : config.agentDisplayName}
				description={config.headerDescription}
			>
				<div className="flex items-center gap-2">
					<Button variant="ghost" size="sm" onClick={() => setRotateOpen(true)} className="gap-1.5 text-gray-500">
						<RefreshCw className="w-3.5 h-3.5" /> New chat
					</Button>
					<Button variant="outline" onClick={onClose} className="gap-2">
						<ArrowLeft className="w-4 h-4" /> {config.backLabel}
					</Button>
				</div>
			</PageHeader>

			{/* Mobile view toggle */}
			<div className="mb-3 flex gap-1 rounded-lg bg-gray-100 p-1 lg:hidden">
				<button onClick={() => setMobileView('chat')} className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium ${mobileView === 'chat' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>Chat</button>
				<button onClick={() => setMobileView('board')} className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium ${mobileView === 'board' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>Status &amp; artifacts</button>
			</div>

			<div ref={splitRef} className="flex gap-2" style={{ height: 'calc(100dvh - 240px)', minHeight: 420, ['--chat-w' as string]: `${splitPct}%` } as React.CSSProperties}>
				{/* Chat column */}
				<div
					className={`relative min-w-0 flex-1 lg:flex-none lg:w-[var(--chat-w)] flex-col overflow-hidden rounded-lg border border-gray-200 bg-gray-50 ${mobileView === 'chat' ? 'flex' : 'hidden'} lg:flex ${dragging ? 'pointer-events-none select-none' : ''}`}
					onDragEnter={(e) => { e.preventDefault(); dragDepth.current += 1; setDragOver(true); }}
					onDragOver={(e) => { e.preventDefault(); }}
					onDragLeave={() => { dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragOver(false); }}
					onDrop={(e) => { e.preventDefault(); dragDepth.current = 0; setDragOver(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); }}
				>
					{/* Full-column drop indicator */}
					{dragOver && (
						<div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg border-2 border-dashed border-primary-400 bg-primary-50/90">
							<div className="flex flex-col items-center gap-2 text-primary-700">
								<Paperclip className="h-8 w-8" />
								<span className="text-sm font-semibold">{config.dropLabel}</span>
							</div>
						</div>
					)}
					{/* Back-to-latest */}
					{!atBottom && (
						<button
							onClick={() => {
								setAtBottom(true);
								bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
							}}
							className="absolute bottom-24 left-1/2 z-10 -translate-x-1/2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 shadow-md hover:bg-gray-50"
						>
							↓ Latest
						</button>
					)}
					{!linkId ? (
						<div className="flex flex-1 items-center justify-center"><Spinner /></div>
					) : (
						<>
							<div
								ref={messagesRef}
								onScroll={() => {
									const el = messagesRef.current;
									if (!el) return;
									setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 120);
								}}
								className="flex-1 space-y-3 overflow-y-auto p-4"
							>
								{visible.length === 0 && !isProcessing && (
									<div className="mx-auto mt-10 max-w-md text-center text-sm text-gray-400">
										<Bot className="mx-auto mb-3 h-10 w-10 text-gray-300" />
										{config.emptyStateHint}
									</div>
								)}
								{visible.map(renderMessage)}

								{/* Live turn overlay */}
								{partialText && (
									<div className="flex gap-2.5">
										<div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
											<Bot className="w-4 h-4" />
										</div>
										<div className="max-w-[85%] rounded-2xl rounded-bl-md border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-800">
											<div className="text-sm leading-relaxed [&_p]:my-1.5 [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:text-[12px] [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-gray-900 [&_pre]:p-2 [&_pre]:text-gray-100 [&_table]:my-2 [&_table]:w-full [&_table]:text-xs [&_th]:border [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1 [&_a]:text-primary-600 [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-500">
												<ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{partialText}</ReactMarkdown>
											</div>
										</div>
									</div>
								)}
								{isProcessing && (
									<div className="flex gap-2.5">
										<div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
											<Bot className="w-4 h-4 animate-pulse" />
										</div>
										<div className="flex items-center gap-2.5 rounded-2xl rounded-bl-md border border-gray-200 bg-white px-4 py-2.5">
											<span className="flex gap-1">
												<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 [animation-delay:0ms]" />
												<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 [animation-delay:150ms]" />
												<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary-500 [animation-delay:300ms]" />
											</span>
											<span className="text-sm text-gray-600">
												{partialText ? 'Writing the reply' : thinking ? 'Thinking it through' : activity ? activity.label : 'Working'}
											</span>
											{activity && activity.steps > 1 && (
												<span className="text-xs text-gray-400">step {activity.steps}</span>
											)}
											{elapsed >= 5 && <span className="text-xs tabular-nums text-gray-400">{elapsed}s</span>}
										</div>
									</div>
								)}
							{queueItems.map(qi => (
									<div key={qi.id} className="flex justify-end">
										<div className="max-w-[85%] rounded-2xl rounded-br-md border border-dashed border-amber-400 bg-amber-50 px-4 py-2.5 text-sm text-gray-700">
											{(qi.message || qi.preview || 'Queued message').slice(0, 400)}
											<div className="mt-1.5 flex items-center gap-3">
												<span className="text-[11px] font-medium uppercase tracking-wide text-amber-600">Queued</span>
												<button
													onClick={async () => {
														if (!linkId) return;
														try { await svc.runQueuedNow(linkId, qi.id); } catch (e) { notifyError(e instanceof Error ? e.message : 'Could not run it now'); }
														void refreshQueue();
														refetchTranscript();
													}}
													className="inline-flex items-center gap-1 rounded-full border border-amber-400 bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100"
													title="Stop the current task and run this now"
												>
													<Play className="h-3 w-3" /> Run now
												</button>
												<button
													onClick={async () => {
														if (!linkId) return;
														try { await svc.cancelQueued(linkId, qi.id); } catch (e) { notifyError(e instanceof Error ? e.message : 'Could not cancel it'); }
														void refreshQueue();
													}}
													className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-500 hover:bg-gray-100"
													title="Cancel this queued message"
												>
													<X className="h-3 w-3" /> Cancel
												</button>
											</div>
										</div>
									</div>
								))}
								<div ref={bottomRef} />
							</div>

							{/* Helpers at work — the ONLY place internal helper activity surfaces */}
							{(helpersCount > 0 || helperUpdateCount > 0) && (
								<div className="flex items-center gap-2 border-t border-gray-200 bg-secondary-50 px-4 py-1.5 text-xs font-medium text-secondary-700">
									<span className="relative flex h-2 w-2">
										<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary-400 opacity-60" />
										<span className="relative inline-flex h-2 w-2 rounded-full bg-secondary-500" />
									</span>
									{helpersCount > 0
										? `${helpersCount === 1 ? 'Subagent is' : `${helpersCount} subagents are`} working`
										: 'Subagent results are coming in'}
									{helperUpdateCount > 0 && ` · ${helperUpdateCount} result${helperUpdateCount === 1 ? '' : 's'} just in`}
									{' '}— picked up automatically
								</div>
							)}
							{/* Composer */}
							<div className={`border-t bg-white p-3 ${dragOver ? 'border-primary-400 ring-2 ring-primary-200' : 'border-gray-200'}`}>
								{showRetryBanner && (
									<div className="mb-2 flex items-center gap-3 rounded-lg border border-orange-300 bg-orange-50 px-3 py-1.5 text-sm text-orange-700" role="alert">
										<span>Transcription failed</span>
										<button type="button" onClick={() => void handleVoiceRetry()} className="rounded-md bg-orange-500 px-2 py-0.5 text-xs font-medium text-white hover:bg-orange-600">
											Retry
										</button>
										<button type="button" onClick={() => useAgentVoiceStore.getState().setShowRetryBanner(false)} className="rounded-md px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700">
											Dismiss
										</button>
									</div>
								)}
								{stagedFiles.length > 0 && (
									<div className="mb-2 flex flex-wrap gap-1.5">
										{stagedFiles.map((f, i) => (
											<span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-700">
												<FileText className="h-3 w-3 text-gray-400" /> {f.name}
												<button onClick={() => setStagedFiles(prev => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-gray-600">
													<X className="h-3 w-3" />
												</button>
											</span>
										))}
									</div>
								)}
								{/* Composer (2026-10-26): the message box gets the full width, the
								    controls sit on one row under it, so on phones the box is no
								    longer squeezed between attach, mic and Send/Stop. */}
								<div className="rounded-xl border border-gray-300 bg-white focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
									<input
										ref={fileInputRef}
										type="file"
										multiple
										accept="application/pdf,.pdf,image/*,.png,.jpg,.jpeg,.webp,.heic"
										className="hidden"
										onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }}
									/>
									<textarea
										value={input}
										onChange={(e) => setInput(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); }
										}}
										ref={textareaRef}
										rows={1}
										placeholder={config.composerPlaceholder}
										className="block max-h-40 w-full resize-none border-0 bg-transparent px-3 pt-2.5 pb-1 text-base sm:text-sm focus:outline-none focus:ring-0"
									/>
									<div className="flex items-center justify-between gap-2 px-1.5 pb-1.5">
										<button
											onClick={() => fileInputRef.current?.click()}
											className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
											title="Attach files"
											aria-label="Attach files"
										>
											<Paperclip className="h-5 w-5" />
										</button>
										<div className="flex items-center gap-1.5">
											<VoiceButton
												state={voiceState}
												duration={recordingDuration}
												onClick={() => void handleVoiceClick()}
												audioDevices={audioDevices}
												selectedDeviceId={selectedDeviceId}
												onSelectDevice={setSelectedDevice}
												disabled={!linkId}
											/>
											{isProcessing ? (
												<Button variant="outline" size="sm" onClick={() => void stop()} className="gap-1.5 whitespace-nowrap" title="Stop the agent" aria-label="Stop">
													<Square className="h-3.5 w-3.5" /><span className="hidden sm:inline">Stop</span>
												</Button>
											) : (
												<Button size="sm" onClick={() => void send()} disabled={sending || (!input.trim() && stagedFiles.length === 0)} className="gap-1.5" title="Send" aria-label="Send">
													{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}<span className="hidden sm:inline">Send</span>
												</Button>
											)}
										</div>
									</div>
								</div>
							</div>
						</>
					)}
				</div>

				{/* Right panel — board + artifacts */}
				{/* Drag handle — desktop only */}
			<div
				onPointerDown={(e) => { e.preventDefault(); setDragging(true); }}
				className="hidden lg:flex w-2 shrink-0 cursor-col-resize items-center justify-center group"
				title="Drag to resize"
			>
				<div className={`h-16 w-1 rounded-full ${dragging ? 'bg-primary-400' : 'bg-gray-200 group-hover:bg-primary-300'}`} />
			</div>

			<div className={`w-full lg:flex-1 lg:min-w-0 ${mobileView === 'board' ? 'block' : 'hidden'} lg:block ${dragging ? 'pointer-events-none select-none' : ''}`}>
					{rightPanel}
				</div>
			</div>

			<ConfirmDialog
				isOpen={rotateOpen}
				onClose={() => setRotateOpen(false)}
				onConfirm={() => void doRotate()}
				title="Start a fresh chat?"
				description="The current conversation is archived and the agent starts clean for this event. Records already written are not affected."
				confirmText="Start fresh"
			/>
		</PageContainer>
	);
}
